import React, { FC, ReactElement, ReactNode, useMemo, useRef, useState } from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import LinearProgress from '@material-ui/core/LinearProgress'
import Paper from '@material-ui/core/Paper'
import Popper from '@material-ui/core/Popper'
import Switch from '@material-ui/core/Switch'
import Tooltip from '@material-ui/core/Tooltip'
import SearchIcon from '@material-ui/icons/Search'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { LicenceManagementPageLanguage } from '../../../../../../i18n'
import { isStringNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../../../../React/Common/Constants/Separators'
import Icon, { IconName } from '../../../../../React/Common/Components/Icon'
import { SelectOption } from '../../../../../React/Common/Components/Selects/Types'
import { Modal } from '../../../Molecules/Modal/Modal'
import { OutlinedSelect } from '../../../Atoms/Inputs/OutlinedSelect'
import { OutlinedTextInput } from '../../../Atoms/Inputs/OutlinedTextInput'
import { RouterCheckbox } from '../../../Atoms/Inputs/RouterCheckbox'
import { RoundedButton } from '../../../Atoms/RoundedButton/RoundedButton'
import { getSharedLicenceDisplayValue, USER_LICENCE_LANGUAGE_KEY_BY_VALUE } from '../licenceAssignment'
import { BulkLicence } from './bulkLicenceProfiles'
import { BlockerCode, BulkGroupSummary, BulkRow, bulkBlockerText, isItemLicensed } from './bulkAssignRules'
import { BulkRowStatus, BulkRowStatusKind, useBulkAssign, UseBulkAssign } from './useBulkAssign'
import { RowWindow, useWindowedRows } from './useWindowedRows'

export type BulkAssignDialogProps = {
    readonly language: CLanguageHandler
    /** CME the page is showing; pre-selected in the dialog. */
    readonly initialCmeId: string
    /** Telsys the page is showing, if a single one is selected; pre-selected in the dialog. */
    readonly initialTelSysId: Nullable<string | number>
    readonly isCxiDataEnabled: boolean
    /** Called when the dialog closes; `hasChanges` is true once any licence has been assigned. */
    readonly onClose: (hasChanges: boolean) => void
}

type PillTone = 'muted' | 'busy' | 'ok' | 'warn' | 'err'

const TONE_BY_STATUS_KIND: Readonly<Record<BulkRowStatusKind, PillTone>> = {
    [BulkRowStatusKind.WAITING]: 'muted',
    [BulkRowStatusKind.WORKING]: 'busy',
    [BulkRowStatusKind.DONE]: 'ok',
    [BulkRowStatusKind.DONE_WARNING]: 'warn',
    [BulkRowStatusKind.FAILED]: 'err',
    [BulkRowStatusKind.NOT_RUN]: 'warn'
}

const StatusPill: FC<{ readonly tone: PillTone; readonly text: string }> = ({
    tone,
    text
}: {
    readonly tone: PillTone
    readonly text: string
}): ReactElement => (
    <span className={`lmp-bulk-pill lmp-bulk-pill--${tone}`} title={text}>
        {text}
    </span>
)

const Section: FC<{ readonly title: string; readonly step: number; readonly children: ReactNode }> = ({
    title,
    step,
    children
}: {
    readonly title: string
    readonly step: number
    readonly children: ReactNode
}): ReactElement => (
    <section className='lmp-bulk-section'>
        <h3 className='lmp-bulk-section__title'>
            <span className='lmp-bulk-section__step' aria-hidden='true'>
                {step}
            </span>
            {title}
        </h3>
        {children}
    </section>
)

/** CCS fills a missing full name with a bracketed placeholder, e.g. "[Full Name Not Specified]". */
const PLACEHOLDER_NAME_PATTERN: RegExp = /^\[.*\]$/

const isPlaceholderName = (value: Definable<string>): boolean =>
    !isStringNotEmpty(value) || PLACEHOLDER_NAME_PATTERN.test((value ?? EMPTY_STRING).trim())

/** Shown in a cell that has no value. */
const NO_VALUE: string = '—'

type GroupFilterProps = {
    readonly bulk: UseBulkAssign
    readonly language: CLanguageHandler
}

/** The groups button and its searchable popover, split into security groups and call queues. */
const GroupFilter: FC<GroupFilterProps> = ({ bulk, language }: GroupFilterProps): ReactElement => {
    const anchorRef = useRef<HTMLButtonElement>(null)
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const query: string = bulk.groupSearch.trim().toLowerCase()
    const shown: readonly BulkGroupSummary[] = bulk.groups
        .filter(
            (group: BulkGroupSummary): boolean =>
                bulk.showLicensed || group.unlicensed > 0 || bulk.selectedGroups.has(group.id)
        )
        .filter(
            (group: BulkGroupSummary): boolean =>
                query === EMPTY_STRING ||
                group.label.toLowerCase().includes(query) ||
                group.id.toLowerCase().includes(query)
        )
    const securityGroups: readonly BulkGroupSummary[] = shown.filter(
        (group: BulkGroupSummary): boolean => !group.isQueue
    )
    const callQueues: readonly BulkGroupSummary[] = shown.filter((group: BulkGroupSummary): boolean => group.isQueue)
    const firstSelected: Definable<BulkGroupSummary> = bulk.groups.find((group: BulkGroupSummary): boolean =>
        bulk.selectedGroups.has(group.id)
    )

    let buttonText: string

    if (bulk.isLoadingGroups) {
        buttonText = language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_Loading)
    } else if (bulk.selectedGroups.size === 0) {
        buttonText = language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_All)
    } else if (bulk.selectedGroups.size === 1) {
        buttonText = language.getInterpolated(
            LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_One,
            firstSelected?.label ?? EMPTY_STRING
        )
    } else {
        buttonText = language.getInterpolated(
            LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_Many,
            bulk.selectedGroups.size
        )
    }

    const renderGroup = (group: BulkGroupSummary): ReactElement => (
        <label key={group.id} className='lmp-bulk-groups__item'>
            <RouterCheckbox
                isChecked={bulk.selectedGroups.has(group.id)}
                onChange={(): void => bulk.toggleGroup(group.id)}
            />
            <span className='lmp-bulk-groups__label' title={group.id}>
                {group.isQueue && group.label !== group.id ? `${group.label}  (${group.id})` : group.label}
            </span>
            <span className='lmp-bulk-groups__count'>{bulk.showLicensed ? group.total : group.unlicensed}</span>
        </label>
    )

    return (
        <>
            <button
                ref={anchorRef}
                type='button'
                className={`lmp-bulk-groups__button ${
                    bulk.selectedGroups.size > 0 ? 'lmp-bulk-groups__button--active' : EMPTY_STRING
                }`}
                disabled={bulk.isLoadingGroups || bulk.groups.length === 0}
                aria-haspopup='dialog'
                aria-expanded={isOpen}
                aria-label={buttonText}
                title={buttonText}
                onClick={(): void => setIsOpen(!isOpen)}
            >
                <span className='lmp-bulk-groups__button-text' aria-hidden='true'>
                    {bulk.selectedGroups.size > 0
                        ? language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_Label)
                        : buttonText}
                </span>
                {bulk.selectedGroups.size > 0 && (
                    <span className='lmp-bulk-groups__badge' aria-hidden='true'>
                        {bulk.selectedGroups.size}
                    </span>
                )}
                <Icon iconName={IconName.ARROW_DROPDOWN} />
            </button>
            <Popper
                open={isOpen}
                anchorEl={anchorRef.current}
                placement='bottom-end'
                className='lmp-bulk-groups-popper'
                modifiers={{
                    flip: { enabled: true },
                    preventOverflow: { enabled: true, boundariesElement: 'viewport' }
                }}
            >
                <ClickAwayListener
                    onClickAway={(event: React.MouseEvent<Document>): void => {
                        if (anchorRef.current?.contains(event.target as Node)) return

                        setIsOpen(false)
                    }}
                >
                    <Paper elevation={8} className='lmp-bulk-groups' role='dialog'>
                        <input
                            className='lmp-bulk-input lmp-bulk-groups__search'
                            aria-label={language.get(
                                LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_Search
                            )}
                            placeholder={language.get(
                                LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_Search
                            )}
                            value={bulk.groupSearch}
                            autoFocus
                            onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                bulk.setGroupSearch(event.target.value)
                            }
                        />
                        <div className='lmp-bulk-groups__list'>
                            {securityGroups.length > 0 && (
                                <div className='lmp-bulk-groups__heading'>
                                    {language.get(
                                        LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_Security
                                    )}
                                </div>
                            )}
                            {securityGroups.map(renderGroup)}
                            {callQueues.length > 0 && (
                                <div className='lmp-bulk-groups__heading'>
                                    {language.get(
                                        LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_Queues
                                    )}
                                </div>
                            )}
                            {callQueues.map(renderGroup)}
                            {shown.length === 0 && (
                                <div className='lmp-bulk-muted lmp-bulk-groups__empty'>
                                    {language.get(
                                        bulk.groups.length > 0
                                            ? LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_NoMatch
                                            : LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_NoneLoaded
                                    )}
                                </div>
                            )}
                        </div>
                        <div className='lmp-bulk-groups__footer'>
                            <span className='lmp-bulk-muted lmp-bulk-grow'>
                                {language.get(
                                    bulk.showLicensed
                                        ? LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_CountAll
                                        : LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_CountUnlicensed
                                )}
                            </span>
                            <RoundedButton isOutlined shrink onClick={bulk.clearGroups}>
                                {language.get(
                                    LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_Clear
                                )}
                            </RoundedButton>
                            <RoundedButton shrink onClick={(): void => setIsOpen(false)}>
                                {language.get(
                                    LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Groups_Done
                                )}
                            </RoundedButton>
                        </div>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    )
}

/**
 * Bulk licence assignment dialog for the Licence Management page (APD-13993): pick a telephony
 * system, load its users, filter and select them, and give each the same licence in one run.
 */
export const BulkAssignDialog: FC<BulkAssignDialogProps> = ({
    language,
    initialCmeId,
    initialTelSysId,
    isCxiDataEnabled,
    onClose
}: BulkAssignDialogProps): ReactElement => {
    const bulk: UseBulkAssign = useBulkAssign({ language, initialCmeId, initialTelSysId, isCxiDataEnabled })
    // Only the rows in view are rendered, so a system with hundreds of users stays responsive. Selection
    // and select-all still work on the hook's full list, so rows out of view are still ticked and run.
    const rowKeys: readonly string[] = useMemo(
        (): readonly string[] => bulk.visibleRows.map((row: BulkRow): string => row.key),
        [bulk.visibleRows]
    )
    const rowWindow: RowWindow = useWindowedRows(rowKeys)
    const text = (key: string): string => language.get(key)
    const L = LicenceManagementPageLanguage

    const handleClose = (): void => {
        if (bulk.isRunning) return

        onClose(bulk.hasChanges)
    }

    const licenceOptions: SelectOption<string>[] = bulk.profile.offered.map(
        (licence: BulkLicence): SelectOption<string> => ({ label: bulk.licenceLabel(licence), value: licence })
    )

    const currentLicenceText = (row: BulkRow): string => {
        const value: Definable<string> = bulk.assignedLicence(row) ?? row.item.UserLicence

        if (!isItemLicensed({ UserLicence: value })) return text(L.LicenceManagementPage_BulkAssign_NoLicence)

        return getSharedLicenceDisplayValue(
            value,
            language,
            USER_LICENCE_LANGUAGE_KEY_BY_VALUE,
            bulk.profile.telSysType,
            bulk.profile.integrationType
        )
    }

    const renderStatus = (row: BulkRow, blocker: Nullable<BlockerCode>): ReactNode => {
        const status: Definable<BulkRowStatus> = bulk.statusFor(row)

        if (status) {
            return (
                <div className='lmp-bulk-status'>
                    <StatusPill tone={TONE_BY_STATUS_KIND[status.kind]} text={status.text} />
                    {status.detail && isStringNotEmpty(status.detail.text) && (
                        <div
                            className='lmp-bulk-error'
                            title={[status.detail.raw, status.detail.meta].filter(isStringNotEmpty).join(' — ')}
                        >
                            {status.detail.text}
                        </div>
                    )}
                    {status.detail && isStringNotEmpty(status.detail.meta) && (
                        <div className='lmp-bulk-error-meta'>{status.detail.meta}</div>
                    )}
                </div>
            )
        }

        if (isItemLicensed(row.item)) {
            return (
                <div className='lmp-bulk-status'>
                    <StatusPill tone='muted' text={text(L.LicenceManagementPage_BulkAssign_Status_Licensed)} />
                </div>
            )
        }

        if (blocker) {
            return (
                <div className='lmp-bulk-status'>
                    <StatusPill tone='warn' text={bulkBlockerText(language, blocker, bulk.profile)} />
                </div>
            )
        }

        return (
            <div className='lmp-bulk-status'>
                <StatusPill tone='ok' text={text(L.LicenceManagementPage_BulkAssign_Status_Ready)} />
            </div>
        )
    }

    const renderEmailCell = (row: BulkRow): ReactNode => {
        const licensed: boolean = isItemLicensed(row.item)

        if (!isStringNotEmpty(row.prefilledEmail) && !licensed && !bulk.isDone(row)) {
            return (
                <input
                    className='lmp-bulk-input lmp-bulk-cell-input'
                    placeholder={text(L.LicenceManagementPage_BulkAssign_EnterEmail)}
                    aria-label={text(L.LicenceManagementPage_BulkAssign_EnterEmail)}
                    value={bulk.emailFor(row)}
                    disabled={bulk.isRunning}
                    onClick={(event: React.MouseEvent): void => event.stopPropagation()}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                        bulk.setEmail(row, event.target.value)
                    }
                />
            )
        }

        const username: string = bulk.usernameFor(row)

        return (
            <span className='lmp-bulk-user' title={username}>
                {row.exists && !licensed && (
                    <Tooltip title={text(L.LicenceManagementPage_BulkAssign_UserExists)} placement='top' arrow>
                        <span
                            className='lmp-bulk-user__warning'
                            role='img'
                            aria-label={text(L.LicenceManagementPage_BulkAssign_UserExists)}
                        >
                            <Icon iconName={IconName.WARNING} />
                        </span>
                    </Tooltip>
                )}
                <span className='lmp-bulk-user__text'>{username || NO_VALUE}</span>
            </span>
        )
    }

    const renderRow = (row: BulkRow): ReactElement => {
        const blocker: Nullable<BlockerCode> = bulk.blockerFor(row)
        const selectable: boolean = bulk.isSelectable(row)
        const checked: boolean = bulk.isSelected(row)
        const name: string = bulk.nameFor(row)
        let shownName: string = name

        if (isPlaceholderName(shownName)) {
            shownName = isPlaceholderName(row.row.Description) ? EMPTY_STRING : row.row.Description
        }

        const partition: string = isStringNotEmpty(row.row.PartitionDescription)
            ? row.row.PartitionDescription
            : EMPTY_STRING
        const isLicenceShown: boolean = isItemLicensed(row.item) || bulk.isDone(row)
        const licenceText: string = isLicenceShown
            ? currentLicenceText(row)
            : text(L.LicenceManagementPage_BulkAssign_NoLicenceShort)
        const agent: string = row.row.AgentNumber || (row.row.HasAgent ? row.row.DeviceNumber : EMPTY_STRING)
        const numbers: string[] = [row.row.DeviceNumber, agent !== row.row.DeviceNumber ? agent : EMPTY_STRING].filter(
            isStringNotEmpty
        )
        const isDimmed: boolean = blocker !== null && !isItemLicensed(row.item) && !bulk.isDone(row)

        return (
            <tr
                key={row.key}
                ref={rowWindow.measureRow(row.key)}
                data-testid={`lmp-bulk-row-${row.key}`}
                className={[
                    isDimmed ? 'lmp-bulk-row--dim' : EMPTY_STRING,
                    selectable ? 'lmp-bulk-row--clickable' : EMPTY_STRING,
                    checked ? 'lmp-bulk-row--selected' : EMPTY_STRING
                ].join(' ')}
                onClick={(event: React.MouseEvent): void => {
                    if ((event.target as HTMLElement).closest('input, select, button, a, label, .checkbox')) return

                    bulk.toggleRow(row)
                }}
            >
                <td className='lmp-bulk-table__check'>
                    <RouterCheckbox
                        isChecked={checked}
                        disabled={!selectable}
                        title={language.getInterpolated(L.LicenceManagementPage_BulkAssign_SelectRow, name)}
                        onChange={(): void => bulk.toggleRow(row)}
                    />
                </td>
                <td className='lmp-bulk-table__text' title={shownName}>
                    {isStringNotEmpty(shownName) ? shownName : <span className='lmp-bulk-muted'>{NO_VALUE}</span>}
                </td>
                <td className='lmp-bulk-table__user'>{renderEmailCell(row)}</td>
                <td className='lmp-bulk-table__numbers' title={numbers.join(' · ')}>
                    {numbers.length > 0 ? (
                        numbers.map(
                            (number: string, index: number): ReactElement => (
                                <span key={number} className={index > 0 ? 'lmp-bulk-muted' : EMPTY_STRING}>
                                    {index > 0 ? ` · ${number}` : number}
                                </span>
                            )
                        )
                    ) : (
                        <span className='lmp-bulk-muted'>{NO_VALUE}</span>
                    )}
                </td>
                <td className='lmp-bulk-table__text' title={partition}>
                    {isStringNotEmpty(partition) ? partition : <span className='lmp-bulk-muted'>{NO_VALUE}</span>}
                </td>
                <td className='lmp-bulk-table__text' title={isLicenceShown ? licenceText : currentLicenceText(row)}>
                    <span className={isLicenceShown ? EMPTY_STRING : 'lmp-bulk-muted'}>{licenceText}</span>
                </td>
                <td className='lmp-bulk-table__status'>{renderStatus(row, blocker)}</td>
            </tr>
        )
    }

    // Progress messages and the live tally re-render the dialog many times a second while loading or
    // applying; the rows only change when something they show changes, so keep the rendered window
    // unless one of those inputs (everything renderRow reads) moves.
    const renderedRows: ReactElement[] = useMemo(
        (): ReactElement[] => bulk.visibleRows.slice(rowWindow.start, rowWindow.end).map(renderRow),
        [
            bulk.visibleRows,
            rowWindow.start,
            rowWindow.end,
            rowWindow.measureRow,
            bulk.blockerFor,
            bulk.isSelectable,
            bulk.isSelected,
            bulk.statusFor,
            bulk.emailFor,
            bulk.assignedLicence,
            bulk.isDone,
            bulk.nameFor,
            bulk.usernameFor,
            bulk.isRunning,
            bulk.profile,
            bulk.toggleRow,
            bulk.setEmail,
            language
        ]
    )

    let emptyText: string = text(L.LicenceManagementPage_BulkAssign_Empty_NotLoaded)

    if (bulk.rows.length > 0) {
        emptyText = text(
            bulk.selectedGroups.size > 0
                ? L.LicenceManagementPage_BulkAssign_Empty_NoGroupMatch
                : L.LicenceManagementPage_BulkAssign_Empty_NoMatch
        )
    }

    const selectedCount: number = bulk.selectedRows.length
    const plan = bulk.runPlan
    const isBusy: boolean = bulk.isRunning || bulk.isLoadingUsers

    return (
        <>
            <Modal
                className='lmp-bulk-dialog'
                isOpen={true}
                onClose={handleClose}
                closeOnBackdropClick={false}
                isClosableWithXButton={!bulk.isRunning}
            >
                <div className='lmp-bulk' role='region' aria-label={text(L.LicenceManagementPage_BulkAssign_Title)}>
                    <h2 className='lmp-bulk__title'>{text(L.LicenceManagementPage_BulkAssign_Title)}</h2>

                    <div className='lmp-bulk__body'>
                        <Section step={1} title={text(L.LicenceManagementPage_BulkAssign_Step_System)}>
                            <div className='lmp-bulk-row'>
                                <OutlinedSelect<string>
                                    containerClassName='lmp-bulk-field lmp-bulk-field--cme'
                                    label={text(L.LicenceManagementPage_BulkAssign_Cme)}
                                    options={bulk.cmeOptions}
                                    value={bulk.cmeId}
                                    disabled={isBusy}
                                    handleChange={bulk.selectCme}
                                />
                                <OutlinedSelect<string>
                                    containerClassName='lmp-bulk-field lmp-bulk-field--telsys'
                                    label={text(L.LicenceManagementPage_BulkAssign_TelSys)}
                                    options={bulk.telSysOptions}
                                    value={bulk.telSysId}
                                    disabled={isBusy}
                                    handleChange={bulk.selectTelSys}
                                />
                                <RoundedButton
                                    className='lmp-bulk-button lmp-bulk-load'
                                    disabled={isBusy || bulk.isLoadingGroups || bulk.isResolvingType}
                                    onClick={(): void => {
                                        bulk.loadUsers().catch((): void => undefined)
                                    }}
                                >
                                    {text(L.LicenceManagementPage_BulkAssign_LoadUsers)}
                                </RoundedButton>
                                <span className='lmp-bulk-muted lmp-bulk-load-status' role='status'>
                                    {bulk.loadMessage}
                                </span>
                            </div>
                            <div className='lmp-bulk-muted lmp-bulk-note'>
                                {bulk.systemNote}
                                {bulk.isDetected && ` · ${text(L.LicenceManagementPage_BulkAssign_Detected)}`}
                            </div>
                        </Section>

                        <Section step={2} title={text(L.LicenceManagementPage_BulkAssign_Step_Options)}>
                            <div className='lmp-bulk-row'>
                                <OutlinedSelect<string>
                                    containerClassName='lmp-bulk-field lmp-bulk-field--licence'
                                    label={text(L.LicenceManagementPage_BulkAssign_Licence)}
                                    options={licenceOptions}
                                    value={bulk.licence}
                                    disabled={bulk.isRunning || bulk.isResolvingType}
                                    handleChange={(value: string): void => bulk.setLicence(value as BulkLicence)}
                                />
                                <div className='lmp-bulk-options'>
                                    <FormControlLabel
                                        className='lmp-bulk-toggle'
                                        control={
                                            <Switch
                                                color='primary'
                                                checked={bulk.welcomeEmail}
                                                disabled={bulk.isRunning}
                                                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                                    bulk.setWelcomeEmail(event.target.checked)
                                                }
                                            />
                                        }
                                        label={text(L.LicenceManagementPage_BulkAssign_WelcomeEmail)}
                                    />
                                </div>
                            </div>
                        </Section>

                        <Section step={3} title={text(L.LicenceManagementPage_BulkAssign_Step_Users)}>
                            <div className='lmp-bulk-row lmp-bulk-toolbar'>
                                <OutlinedTextInput
                                    containerClassName='lmp-bulk-field lmp-bulk-search'
                                    placeholder={text(L.LicenceManagementPage_BulkAssign_Search)}
                                    value={bulk.search}
                                    onChange={bulk.setSearch}
                                    adornmentEnd={<SearchIcon className='lmp-bulk-search__icon' />}
                                />
                                <GroupFilter bulk={bulk} language={language} />
                                <FormControlLabel
                                    className='lmp-bulk-toggle lmp-bulk-toggle--licensed'
                                    control={
                                        <Switch
                                            color='primary'
                                            checked={bulk.showLicensed}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                                bulk.setShowLicensed(event.target.checked)
                                            }
                                        />
                                    }
                                    label={text(L.LicenceManagementPage_BulkAssign_ShowLicensed)}
                                />
                            </div>
                            <div className='lmp-bulk-table-wrap' ref={rowWindow.scrollContainerRef}>
                                <table className='lmp-bulk-table' aria-rowcount={bulk.visibleRows.length + 1}>
                                    <colgroup>
                                        <col className='lmp-bulk-col--check' />
                                        <col className='lmp-bulk-col--name' />
                                        <col className='lmp-bulk-col--user' />
                                        <col className='lmp-bulk-col--device' />
                                        <col className='lmp-bulk-col--partition' />
                                        <col className='lmp-bulk-col--licence' />
                                        <col className='lmp-bulk-col--status' />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th className='lmp-bulk-table__check'>
                                                <RouterCheckbox
                                                    isChecked={bulk.allVisibleSelected}
                                                    disabled={bulk.isRunning || !bulk.anyVisibleSelectable}
                                                    title={text(L.LicenceManagementPage_BulkAssign_SelectAll)}
                                                    onChange={bulk.setAllVisibleSelected}
                                                />
                                            </th>
                                            <th>{text(L.LicenceManagementPage_BulkAssign_Column_Name)}</th>
                                            <th>{text(L.LicenceManagementPage_BulkAssign_Column_Username)}</th>
                                            <th>{text(L.LicenceManagementPage_BulkAssign_Column_Device)}</th>
                                            <th>{text(L.LicenceManagementPage_BulkAssign_Column_Partition)}</th>
                                            <th>{text(L.LicenceManagementPage_BulkAssign_Column_Licence)}</th>
                                            <th>{text(L.LicenceManagementPage_BulkAssign_Column_Status)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bulk.visibleRows.length > 0 ? (
                                            <>
                                                {rowWindow.paddingTop > 0 && (
                                                    <tr aria-hidden='true' className='lmp-bulk-table__spacer'>
                                                        <td colSpan={7} style={{ height: rowWindow.paddingTop }} />
                                                    </tr>
                                                )}
                                                {renderedRows}
                                                {rowWindow.paddingBottom > 0 && (
                                                    <tr aria-hidden='true' className='lmp-bulk-table__spacer'>
                                                        <td colSpan={7} style={{ height: rowWindow.paddingBottom }} />
                                                    </tr>
                                                )}
                                            </>
                                        ) : (
                                            <tr>
                                                <td colSpan={7} className='lmp-bulk-table__empty'>
                                                    {bulk.isLoadingUsers && bulk.rows.length === 0 ? (
                                                        <>
                                                            <CircularProgress size={28} thickness={4} />
                                                            {text(L.LicenceManagementPage_BulkAssign_Loading)}
                                                        </>
                                                    ) : (
                                                        emptyText
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Section>
                    </div>

                    <div className='lmp-bulk-footer lmp-bulk-footer--main'>
                        {(bulk.isRunning || bulk.progress > 0) && (
                            <LinearProgress className='lmp-bulk-progress' variant='determinate' value={bulk.progress} />
                        )}
                        {bulk.runCounts ? (
                            <span className='lmp-bulk-count' role='status' aria-live='polite'>
                                {language.getInterpolated(
                                    L.LicenceManagementPage_BulkAssign_RunProgress,
                                    bulk.runCounts.succeeded,
                                    bulk.runCounts.total,
                                    bulk.runCounts.failed,
                                    bulk.runCounts.total
                                )}
                            </span>
                        ) : (
                            <span className={selectedCount > 0 ? 'lmp-bulk-count' : 'lmp-bulk-count lmp-bulk-muted'}>
                                {selectedCount > 0
                                    ? language.getInterpolated(
                                          L.LicenceManagementPage_BulkAssign_SelectedCount,
                                          selectedCount,
                                          selectedCount,
                                          bulk.licenceLabel(bulk.licence)
                                      )
                                    : text(L.LicenceManagementPage_BulkAssign_NoneSelected)}
                            </span>
                        )}
                        <span className='lmp-bulk-grow' />
                        {!bulk.isRunning && bulk.hasResults && (
                            <RoundedButton className='lmp-bulk-button' isOutlined onClick={bulk.downloadResults}>
                                {text(L.LicenceManagementPage_BulkAssign_DownloadCsv)}
                            </RoundedButton>
                        )}
                        {bulk.isRunning && (
                            <RoundedButton className='lmp-bulk-button' isOutlined onClick={bulk.stop}>
                                {text(L.LicenceManagementPage_BulkAssign_Stop)}
                            </RoundedButton>
                        )}
                        <RoundedButton
                            className='lmp-bulk-button lmp-bulk-run lmp-bulk-run--assign'
                            disabled={bulk.isRunning || selectedCount === 0}
                            onClick={bulk.requestRun}
                        >
                            {language.getInterpolated(
                                L.LicenceManagementPage_BulkAssign_Assign,
                                bulk.isRunning && bulk.runCounts ? bulk.runCounts.total : selectedCount
                            )}
                        </RoundedButton>
                    </div>
                </div>
            </Modal>

            {bulk.isConfirmOpen && (
                <Modal className='lmp-bulk-confirm' isOpen={true} onClose={bulk.cancelConfirm}>
                    <div className='lmp-bulk-confirm__body' role='alertdialog' aria-labelledby='lmp-bulk-confirm-title'>
                        <h3 id='lmp-bulk-confirm-title' className='lmp-bulk-confirm__title'>
                            {language.getInterpolated(L.LicenceManagementPage_BulkAssign_Confirm_Title, plan.total)}
                        </h3>
                        <ul className='lmp-bulk-confirm__list'>
                            <li>
                                {language.getInterpolated(
                                    L.LicenceManagementPage_BulkAssign_Confirm_ByLicence,
                                    plan.total,
                                    bulk.licenceLabel(bulk.licence)
                                )}
                            </li>
                        </ul>
                        <p>
                            {language.getInterpolated(
                                L.LicenceManagementPage_BulkAssign_Confirm_Users,
                                plan.newUsers,
                                plan.existingUsers
                            )}
                        </p>
                        <p>
                            {text(
                                bulk.welcomeEmail
                                    ? L.LicenceManagementPage_BulkAssign_Confirm_WelcomeOn
                                    : L.LicenceManagementPage_BulkAssign_Confirm_WelcomeOff
                            )}
                        </p>
                        {plan.remaining !== null && plan.remaining !== undefined && (
                            <p>
                                {language.getInterpolated(
                                    L.LicenceManagementPage_BulkAssign_Confirm_Remaining,
                                    plan.remaining,
                                    bulk.licenceLabel(bulk.licence)
                                )}
                            </p>
                        )}
                        {plan.shortfall > 0 && (
                            <p className='lmp-bulk-confirm__warning' role='alert'>
                                {language.getInterpolated(
                                    L.LicenceManagementPage_BulkAssign_Confirm_Shortfall,
                                    plan.total - plan.shortfall,
                                    bulk.licenceLabel(bulk.licence),
                                    plan.total,
                                    plan.shortfall
                                )}
                            </p>
                        )}
                        <p className='lmp-bulk-muted lmp-bulk-confirm__note'>
                            {text(L.LicenceManagementPage_BulkAssign_Confirm_Capacity)}
                        </p>
                        <div className='lmp-bulk-footer'>
                            <span className='lmp-bulk-grow' />
                            <RoundedButton className='lmp-bulk-button' isOutlined onClick={bulk.cancelConfirm}>
                                {text(L.LicenceManagementPage_BulkAssign_Confirm_Cancel)}
                            </RoundedButton>
                            <RoundedButton
                                className='lmp-bulk-button lmp-bulk-run lmp-bulk-run--assign'
                                onClick={bulk.confirmRun}
                            >
                                {text(L.LicenceManagementPage_BulkAssign_Confirm_AssignNow)}
                            </RoundedButton>
                        </div>
                    </div>
                </Modal>
            )}

            {bulk.summary && (
                <Modal className='lmp-bulk-confirm' isOpen={true} onClose={bulk.closeSummary}>
                    <div className='lmp-bulk-confirm__body' role='alertdialog' aria-labelledby='lmp-bulk-summary-title'>
                        <h3 id='lmp-bulk-summary-title' className='lmp-bulk-confirm__title'>
                            {text(L.LicenceManagementPage_BulkAssign_Summary_DoneTitle)}
                        </h3>
                        <p className='lmp-bulk-confirm__result'>
                            {language.getInterpolated(
                                L.LicenceManagementPage_BulkAssign_Summary_DoneResult,
                                bulk.summary.succeeded,
                                bulk.summary.failed
                            )}
                        </p>
                        {bulk.summary.stopped && <p>{text(L.LicenceManagementPage_BulkAssign_Summary_Stopped)}</p>}
                        <div className='lmp-bulk-footer'>
                            <span className='lmp-bulk-grow' />
                            <RoundedButton className='lmp-bulk-button' isOutlined onClick={bulk.downloadResults}>
                                {text(L.LicenceManagementPage_BulkAssign_DownloadCsv)}
                            </RoundedButton>
                            <RoundedButton className='lmp-bulk-button' onClick={bulk.closeSummary}>
                                {text(L.LicenceManagementPage_BulkAssign_Summary_Ok)}
                            </RoundedButton>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    )
}
